<template>
  <div
    class="filter-category"
  >
    <div class="d-flex justify-content-between">
      <h6 class="filter-title">
        {{ category.categoryLabel }}
      </h6>
    </div>
    <b-form-group v-if="isLargeCategory">
      <b-form-input
        v-model="search"
        placeholder="Filter"
      />
    </b-form-group>
    <div
      v-if="hasGroups"
      class="filter-checkbox-group"
    >
      <template v-for="entry in flatGroupedItems">
        <div
          v-if="entry.type === 'header'"
          :key="`header-${entry.label}`"
          class="filter-group-label"
        >
          {{ entry.label }}
          <hr class="filter-group-hr">
        </div>
        <b-form-checkbox
          v-else
          :key="`item-${entry.value}`"
          v-model="categoryFilter"
          :value="entry.value"
          :disabled="entry.disabled"
          class="filter-checkbox-item"
          @change="$emit('filter-change', filterResult, category.categoryKey)"
        >
          <span v-html="entry.html || entry.label" />
        </b-form-checkbox>
      </template>
    </div>
    <b-form-checkbox-group
      v-else
      v-model="categoryFilter"
      class="filter-checkbox-group"
      :options="items"
      stacked
      @change="$emit('filter-change', filterResult, category.categoryKey)"
    />
    <div class="text-center pt-1">
      <b-link
        v-if="isLargeCategory && search === ''"
        @click="showMore = !showMore"
      >
        Show {{ showMore ? 'Less' : 'All' }}
      </b-link>
    </div>
  </div>
</template>

<script>
import {
  BFormCheckboxGroup, BFormCheckbox, BFormGroup, BFormInput, BLink,
} from 'bootstrap-vue'

export default {
  components: {
    BFormCheckboxGroup,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BLink,
  },
  props: {
    category: {
      required: true,
      type: Object,
    },
    filter: {
      type: Object,
      required: true,
    },
    clear: {
      type: Boolean,
      required: false,
    },
    isAutocallTabActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: '',
      showMore: false,
    }
  },
  computed: {
    categoryFilter: {
      get() {
        const value = this.filter[this.category.categoryKey]
        return Array.isArray(value) ? value : []
      },
      set(value) {
        this.$set(this.filter, this.category.categoryKey, Array.isArray(value) ? value : [])
      },
    },
    isLargeCategory() {
      return this.category.items.length > 15
    },
    filterResult() {
      const result = {}
      Object.entries(this.filter).forEach(([key, value]) => {
        if (value.length) {
          result[key] = value
        }
      })
      return result
    },
    items() {
      let items = []
      if (this.search) {
        items = this.category.items.filter(item => item.label.toLowerCase().search(this.search.toLowerCase()) !== -1)
      } else if (this.isLargeCategory && !this.showMore) {
        let selected = this.filterResult[this.category.categoryKey]
        selected = selected && selected.length > 0 ? selected : []

        items = this.category.items.filter(item => item.pinned || (item.groupLabel && item.groupLabel.toLowerCase() !== 'previous') || selected.includes(item.value))
      } else {
        items = this.category.items
      }

      // Disable non-autocall product types when autocall tab is active
      if (this.isAutocallTabActive && this.category.categoryKey === 'producttype') {
        items = items.map(item => ({
          ...item,
          disabled: !item.label.toLowerCase().includes('autocall'),
        }))
      }

      return items
    },
    hasGroups() {
      return this.category.items.some(item => item.groupLabel)
    },
    groupedItems() {
      const groupMap = {}
      this.items.forEach(item => {
        const label = item.groupLabel || ''
        if (!groupMap[label]) {
          groupMap[label] = { label, items: [] }
        }
        groupMap[label].items.push(item)
      })
      const order = { recent: 1, previous: 2 }
      return Object.values(groupMap).sort((a, b) => {
        const aRank = order[a.label.toLowerCase()] ?? 0
        const bRank = order[b.label.toLowerCase()] ?? 0
        return aRank - bRank
      })
    },
    flatGroupedItems() {
      const flat = []
      this.groupedItems.forEach(group => {
        if (group.label) {
          flat.push({ type: 'header', label: group.label })
        }
        group.items.forEach(item => flat.push({ type: 'item', ...item }))
      })
      return flat
    },
  },
  watch: {
    clear() {
      if (this.clear) {
        this.search = ''
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.filter-heading {
  margin-top: 0.85rem;
  margin-bottom: 1.75rem;
}

.filter-category{
  margin-top: 1rem;

  &:first-child {
    margin-top: 0 !important;
  }
}

.filter-title {
  margin-bottom: 1rem;
}

.filter-checkbox-group {
  padding-right: 4px;
  max-height: 400px;
  overflow-y: auto;
}

.filter-checkbox-group::-webkit-scrollbar {
  width: 4px;
  border-radius: 6px;
}

.filter-checkbox-group::-webkit-scrollbar-track {
  background: rgba(26, 115, 232, 0.25);
  border-radius: 6px;
}

.filter-checkbox-group::-webkit-scrollbar-thumb {
  background: rgba(26, 115, 232, 0.85);
}

.filter-checkbox-group::v-deep > .custom-control {
  margin-bottom: 0.75rem;
}

.filter-checkbox-group::v-deep > .custom-control > label {
  width: 100%;
  cursor: pointer;
}

.filter-checkbox-group::v-deep > .custom-control > input {
  cursor: pointer;
}

.filter-checkbox-item {
  margin-bottom: 0.75rem;
}

.filter-group-label {
  font-size: 1rem;
  font-weight: 400;
  color: #6e6b7b;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.filter-group-hr {
  margin-top: 0.25rem;
  margin-bottom: 0.75rem;
}
</style>

<style lang="scss">
.filter-product-count {
  height: min-content;
}
</style>
